import { useEffect, useState, useContext, useCallback } from "react"

//Notifications modules
import {
  noteApiListMutedPost,
  noteApiMuteNotificationsPOST,
} from "./notificationsApi"

//PrimeReact
import { Button } from "primereact/button"
import { Tooltip } from "primereact/tooltip"

// Context
import { UserContext } from "../../context/userContext"

//Formik
import { Formik, Form } from "formik"
import * as Yup from "yup"

//Helpers
import { LogError } from "../../helpers/logger"

//Styles
import myStyles from "../../styles/NotificationsForm.module.css"
import { LoaderMedium } from "../Loaders"
import SettingsTable from "./SettingsTable"
import { classNames } from "primereact/utils"

export default function NotificationSettings() {
  const userCtx = useContext(UserContext)
  const group_id = "All"
  const [loading, setLoading] = useState(true)
  const [showAdvanceSettings, setShowAdvanceSettings] = useState(false)
  const [mutedNotifications, setMutedNotifications] = useState([])
  const validationSchema = Yup.object().shape({})

  const getMutedNotifications = useCallback(async () => {
    setLoading(true)

    try {
      const res = await noteApiListMutedPost(
        userCtx.tokens.getNotificationToken(),
        group_id,
        userCtx.participant_id,
      )

      if (res) {
        if (res.mutedEventTypes) {
          res.mutedEventTypes = res.mutedEventTypes.map((eventType) => ({
            ...eventType,
            channelEmail: !eventType.channelEmail,
            channelSignalR: !eventType.channelSignalR,
            channelSMS: !eventType.channelSMS,
            sound: !eventType.sound,
          }))

          // Add "All Notifications"
          const allNotificationsRow = {
            friendlyName: "All Notifications",
            isAll: true,
            channelEmail: res.mutedEventTypes.every((e) => e.channelEmail),
            channelSignalR: res.mutedEventTypes.every((e) => e.channelSignalR),
            channelSMS: res.mutedEventTypes.every((e) => e.channelSMS),
            sound: res.mutedEventTypes.every((e) => e.sound),
          }

          //Sort
          res.mutedEventTypes = [
            allNotificationsRow,
            ...res.mutedEventTypes,
          ].sort((a, b) => {
            if (a.isAll) return -1 // "All Notifications" allways on top
            if (b.isAll) return 1
            return a.friendlyName.localeCompare(b.friendlyName)
          })
        }

        setMutedNotifications(res)
      } else {
        setMutedNotifications([])
      }
    } catch (error) {
      LogError("Unable to list muted notifications", error)
      userCtx.setNotificationError(
        "Unable to list muted notifications. See console for details.",
      )
    }
    setLoading(false)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userCtx.staff_id])

  useEffect(() => {
    getMutedNotifications()
  }, [getMutedNotifications])

  async function applyMute(values) {
    setLoading(true)
    try {
      const mutedEvents = values.mutedEventTypes
        .filter(
          (eventType) =>
            eventType.friendlyName !== "All Notifications" && // Exclude "All Notifications"
            (!eventType.channelEmail ||
              !eventType.channelSMS ||
              !eventType.channelSignalR ||
              !eventType.sound),
        )
        .map((eventType) => {
          return {
            id: eventType.eventTypeId,
            ...eventType,
            channelEmail: !eventType.channelEmail,
            channelSMS: !eventType.channelSMS,
            channelSignalR: !eventType.channelSignalR,
            sound: !eventType.sound,
          }
        })

      await noteApiMuteNotificationsPOST(
        userCtx.tokens.getNotificationToken(),
        group_id,
        userCtx.participant_id,
        mutedEvents,
      )
      userCtx.setNotificationInformation("Mute settings applied successfully")

      userCtx.setMutedNotifications(values)

      await getMutedNotifications()

      setLoading(false)
      return true
    } catch (error) {
      LogError("Unable to list event types", error)
      userCtx.setNotificationError(
        "Unable to list event types. See console for details",
      )
      setLoading(false)
      return false
    }
  }

  if (loading) return <LoaderMedium />

  return (
    <div>
      <Tooltip target=".submit-disabled-tt" />
      <Formik
        initialValues={mutedNotifications}
        enableReinitialize
        validationSchema={validationSchema}
        onSubmit={(values) => {
          applyMute(values)
        }}
      >
        {({ handleSubmit, dirty, values, setFieldValue }) => (
          <Form onSubmit={handleSubmit} autoComplete="off">
            <div className={myStyles.noteSettingsLayout}>
              <SettingsTable
                values={values?.mutedEventTypes || []}
                setFieldValue={setFieldValue}
                showAdvanceSettings={showAdvanceSettings}
              />
              <Button
                label={
                  !showAdvanceSettings
                    ? "Show advanced settings"
                    : "Hide advanced settings"
                }
                className={classNames(
                  "p-button-text p-button-secondary",
                  myStyles.advance_settings_btn,
                )}
                type="button"
                style={{ padding: "0", height: "22px" }}
                icon={
                  !showAdvanceSettings ? "pi pi-angle-down" : "pi pi-angle-up"
                }
                iconPos="right"
                onClick={() => {
                  setShowAdvanceSettings(!showAdvanceSettings)
                }}
              />
              <div className={"flex justify-content-end pr-3 pt-3 pb-3"}>
                <Button
                  className="p-button-rounded"
                  style={{ width: "80px", height: "45px" }}
                  label="Save"
                  type="submit"
                />
              </div>
            </div>
          </Form>
        )}
      </Formik>
    </div>
  )
}
